import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useReactiveVar } from '@apollo/client';
import styled from '@emotion/styled';
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import {
  GetProjectsDocument,
  GetProjectsQuery,
  GetProjectsQueryVariables,
  Meta,
  Project,
} from '../../generated/graphql';
import globalState from '../../operations/commands';
import { lastAccessedOrganizationVar } from '../../cache';
import { getURLWithQueryAdd } from '../../utility/getURLWithQueryAdd';
import { getPathByOrganizationId } from '../../utility/getPathByOrganizationId';
import { ExperimentListItem } from '../component/compound/ExperimentsList/ExperimentListItem';
import { DrawerCreateExperience, DrawerCreateExperienceSimilar } from '../component/compound/ExperimentsList/Drawer';
import Button from '../component/primitive/Button';
import Spinner from '../component/primitive/Spinner';
import { SelectBox } from '../component/primitive/SelectBox';
import {
  COLOR_BORDER,
  COLOR_BUTTON_BACKGROUND,
  COLOR_BUTTON_TEXT,
  COLOR_EFFECT,
  COLOR_SHADOW,
  COLOR_TEXT_MAIN,
} from '../../theme/colors';
import { useQueryString } from '../../hooks/useQueryString';

type CheckboxStates = {
  singlePageTest: boolean; //ページ修正（UIエディター）
  redirectTest: boolean; //ページ修正（スプリットURL）
  replaceImage: boolean; //画像の差し替え
  displayPopup: boolean; //ポップアップ
  notActive: boolean; //テスト準備中
  competition: boolean; //ラウンド実施中
  interim: boolean; //インターバル
  paused: boolean; //ラウンド一時停止中
  archived: boolean; //テスト終了
  editRequest: boolean; //修正反映待ち（仮）
  variation: boolean; //採用待ち（仮）
  advertising: boolean; //デザイン案を募集中
  closed: boolean; //デザイン案募集が終了（締切日超過）
};

/**
 * エクスペリエンス一覧
 */
const ExperimentsListPage: FC<{
  className?: string;
}> = () => {
  const lastOrg = useReactiveVar(lastAccessedOrganizationVar)!;
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>();
  const [meta, setMeta] = useState<Meta>(); //最新のprojects-response情報
  // input-text
  const [trigger, setTrigger] = useState<boolean>(false); //input type="text" のsubmit
  const [keyword, setKeyword] = useState<string | ''>('');

  // select
  const [type, setType] = useState<'open' | 'targeting' | 'my_team' | 'limited_open' | 'private' | 'all'>('all');
  const [device, setDevice] = useState<'pc' | 'sp' | 'all'>('all');
  const [testingExperience, setTestingExperience] = useState<'except' | 'only' | 'all'>('except');
  const [order, setOrder] = useState<'created_at_desc' | 'created_at_asc' | 'cvr_desc' | 'title_asc'>(
    'created_at_desc',
  );
  const [checkboxStates, setCheckboxStates] = useState<CheckboxStates>({
    singlePageTest: false, //ページ修正（UIエディター）
    redirectTest: false, //ページ修正（スプリットURL）
    replaceImage: false, //画像の差し替え
    displayPopup: false, //ポップアップ
    notActive: false, //テスト準備中
    competition: false, //ラウンド実施中
    interim: false, //インターバル
    paused: false, //ラウンド一時停止中
    archived: false, //テスト終了
    editRequest: false, //修正反映待ち（仮）
    variation: false, //採用待ち（仮）
    advertising: false, //デザイン案を募集中
    closed: false, //デザイン案募集が終了（締切日超過）
  });

  const [isOpenDrawerCreateExperience, setIsOpenDrawerCreateExperience] = useState<boolean>(false);
  const [isOpenDrawerCreateExperienceSimilar, setIsOpenDrawerCreateExperienceSimilar] = useState<boolean>(false);

  const { queryString, setQueryString, currentPage, setCurrentPage } = useQueryString(
    {
      keyword,
      type,
      device,
      testingExperience,
      order,
    },
    checkboxStates,
    lastOrg,
    {
      setKeyword,
      setType,
      setDevice,
      setTestingExperience,
      setOrder,
      setCheckboxStates,
    },
  );

  const getProjects = useQuery<GetProjectsQuery, GetProjectsQueryVariables>(GetProjectsDocument, {
    skip: !queryString,
    variables: {
      organization_id: lastOrg.id ?? '',
      search_string: queryString ? `${queryString}` : '',
    },
    context: { clientName: 'planbcd' },
    onCompleted: (data: GetProjectsQuery): void => {
      const loadedProjects = data.projects;
      const meta = loadedProjects[0].meta;
      if (!data.projects || data.projects.length === 0) return; // No data of Projects
      if (!meta) return; // Meta needs to be present for "load more"

      if (1 < Number(meta.current_page)) {
        //2ページ目以降はデータ追加
        setProjects([...(projects ? projects : []), ...data.projects]);
      } else {
        setProjects(data.projects);
      }
      setMeta(meta);
    },
    onError: (e) => {
      globalState.setErrors({
        message: `error: ${e.message}`,
        name: 'NonFatalError',
        context: { clientName: 'search_console' },
      });
    },
  });

  // 組織変更
  useEffect(() => {
    if (!lastOrg.id) {
      return;
    }

    const path = getPathByOrganizationId(lastOrg.id);
    if (location.pathname.indexOf(lastOrg.id) === -1) {
      navigate(getURLWithQueryAdd(`${location.origin}${path}`, location.search).pathname);
    }
  }, [lastOrg.id]);

  /**
   * 「さらに読み込む」押下
   */
  const onClickReadMore = () => {
    const _searchString = new URLSearchParams(queryString);
    _searchString.delete('page');
    _searchString.set('page', (currentPage + 1).toString());
    setQueryString(_searchString.toString());
    setCurrentPage(currentPage + 1);
  };

  /**
   * 開閉した時だけDrawerを読み込む
   */
  const DrawerCreateExperienceMemo = useMemo(
    () => (
      <DrawerCreateExperience
        getProjects={getProjects}
        organization_id={lastOrg.id}
        setIsOpenDrawerCreateExperience={setIsOpenDrawerCreateExperience}
        isOpenDrawerCreateExperience={isOpenDrawerCreateExperience}
      />
    ),
    [isOpenDrawerCreateExperience],
  );

  /**gi
   * 開閉した時だけDrawerを読み込む
   */
  const DrawerCreateExperienceSimilarMemo = useMemo(
    () => (
      <DrawerCreateExperienceSimilar
        getProjects={getProjects}
        organization_id={lastOrg.id}
        setIsOpenDrawerCreateExperienceSimilar={setIsOpenDrawerCreateExperienceSimilar}
        isOpenDrawerCreateExperienceSimilar={isOpenDrawerCreateExperienceSimilar}
      />
    ),
    [isOpenDrawerCreateExperienceSimilar],
  );

  const readMoreButtonMemo = useMemo(() => {
    if (!meta) {
      return null;
    }
    if (Number(meta.current_page) * 50 < Number(meta.total_count)) {
      if (!getProjects.loading) {
        return (
          <ReadMore>
            <SecondaryButton onClick={onClickReadMore}>さらに読み込む</SecondaryButton>
          </ReadMore>
        );
      } else if (currentPage !== 1) {
        return (
          <ReadMore>
            <Spinner />
          </ReadMore>
        );
      }
      return null;
    }
    return null;
  }, [getProjects.loading, meta]);

  return (
    <Root>
      {DrawerCreateExperienceMemo}
      {DrawerCreateExperienceSimilarMemo}
      <Container>
        <Content>
          <Header>
            <Title>エクスペリエンス一覧</Title>
          </Header>
          <HeaderButtonWrapper>
            <PrimaryButton onClick={() => setIsOpenDrawerCreateExperience(true)}>
              エクスペリエンスを作成する
            </PrimaryButton>
          </HeaderButtonWrapper>

          {/* 表示条件の設定 */}
          <StyledPaper>
            <InnerTitleWrapper>
              <PaperTitle>表示条件の設定</PaperTitle>
            </InnerTitleWrapper>

            {/* キーワード検索 */}
            <BoxRowWrapper>
              <div>キーワード検索</div>
              <Input
                type="text"
                onBlur={() => setTrigger(!trigger)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setKeyword(e.target.value);
                }}
                value={keyword}
              />
            </BoxRowWrapper>

            {/* セレクトボックス検索 */}
            <BoxRowWrapper>
              <SelectBox
                title="オファー対象"
                option={[
                  { label: 'すべてのオファー', value: 'all' },
                  { label: 'オープンオファー', value: 'open' },
                  { label: 'ターゲティングオファー', value: 'targeting' },
                  { label: 'マイチームオファー', value: 'my_team' },
                  { label: 'グロースチームオファー', value: 'limited_open' },
                  { label: 'プライベートオファー', value: 'private' },
                ]}
                value={type}
                setValue={setType}
                isLoading={getProjects.loading}
              />

              <SelectBox
                title="デバイス"
                option={[
                  { label: 'すべてのデバイス', value: 'all' },
                  { label: 'PCのみ', value: 'pc' },
                  { label: 'スマートフォンのみ', value: 'sp' },
                ]}
                value={device}
                setValue={setDevice}
                isLoading={getProjects.loading}
              />

              <SelectBox
                title="検証用フラグ"
                option={[
                  { label: 'すべて', value: 'all' },
                  { label: '検証用を除く', value: 'except' },
                  { label: '検証用のみ', value: 'only' },
                ]}
                value={testingExperience}
                setValue={setTestingExperience}
                isLoading={getProjects.loading}
              />
            </BoxRowWrapper>

            {/* チェックボックス検索 */}
            <CheckBoxWrapper>
              {/* 1行目 */}
              <BoxRowWrapper>
                <CheckBoxRowTitle>テスト対象</CheckBoxRowTitle>
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.singlePageTest}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, singlePageTest: e.target.checked });
                      }}
                    />
                  }
                  label="ページ修正（UIエディター）"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.redirectTest}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, redirectTest: e.target.checked });
                      }}
                    />
                  }
                  label="ページ修正（スプリットURL）"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.replaceImage}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, replaceImage: e.target.checked });
                      }}
                    />
                  }
                  label="画像の差し替え"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.displayPopup}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, displayPopup: e.target.checked });
                      }}
                    />
                  }
                  label="ポップアップ"
                />
              </BoxRowWrapper>

              {/* 2行目 */}
              <BoxRowWrapper>
                <CheckBoxRowTitle>ステータス</CheckBoxRowTitle>
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.notActive}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, notActive: e.target.checked });
                      }}
                    />
                  }
                  label="テスト準備中"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.competition}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, competition: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="ラウンド実施中"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.interim}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, interim: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="インターバル"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.paused}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, paused: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="ラウンド一時停止中"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.archived}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, archived: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="テスト終了"
                />
              </BoxRowWrapper>

              {/* 3行目 */}
              <BoxRowWrapper>
                <CheckBoxRowTitle>デザイン案</CheckBoxRowTitle>
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.editRequest}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, editRequest: e.target.checked });
                      }}
                    />
                  }
                  label="修正反映待ち（仮）"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.variation}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, variation: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="採用待ち（仮）"
                />
                があるエクスペリエンスのみを表示する
              </BoxRowWrapper>

              {/* 4行目 */}
              <BoxRowWrapper>
                <CheckBoxRowTitle>デザイン案の募集</CheckBoxRowTitle>
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.advertising}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, advertising: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="デザイン案を募集中"
                />
                <FormControlLabelMui
                  control={
                    <Checkbox
                      disabled={getProjects.loading}
                      checked={checkboxStates.closed}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setCheckboxStates({ ...checkboxStates, closed: e.target.checked });
                      }}
                      color="primary"
                    />
                  }
                  label="デザイン案募集が終了（締切日超過）"
                />
              </BoxRowWrapper>
            </CheckBoxWrapper>
          </StyledPaper>

          {/* ペーパー要素のタイトル */}
          <ListTitleWrapper>
            {meta && !getProjects.loading ? (
              <PaperTitle>{`表示エクスペリエンス ${meta.total_count}件`}</PaperTitle>
            ) : (
              <PaperTitle>
                表示エクスペリエンス
                <SpinnerPosition>
                  <Spinner width="18px" height="18px" size={18} />
                </SpinnerPosition>
              </PaperTitle>
            )}

            {/* セレクトボックス_ソート用 */}
            <SelectBox
              title=""
              option={[
                { label: '作成日時が新しい順', value: 'created_at_desc' },
                { label: '作成日時が古い順', value: 'created_at_asc' },
                { label: '改善率の高い順', value: 'cvr_desc' },
                { label: 'タイトル順', value: 'title_asc' },
              ]}
              value={order}
              setValue={setOrder}
              isLoading={getProjects.loading}
              position="absolute"
            />
          </ListTitleWrapper>

          {/* エクスペリエンス一覧 */}
          {currentPage === 1 && getProjects.loading ? (
            <Spinner />
          ) : (
            projects?.map((project) => <ExperimentListItem project={project} key={`experiment_${project.id}`} />)
          )}

          {/* さらに読み込む */}
          {readMoreButtonMemo}
        </Content>
      </Container>
    </Root>
  );
};

const Root = styled('div')(`
  max-width: 1088px;
  margin: auto auto;
`);

const Container = styled('div')(`
  padding: 36px 0;
`);

const Content = styled('div')(`
  position: relative;
`);

const Header = styled('div')(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 36px;
`);

const HeaderButtonWrapper = styled('div')(`
  position: absolute;
  right: 0;
  top: -10px;
`);

const Title = styled(Typography)(`
  font-weight: 700;
  font-size: 24px;
  color: ${COLOR_TEXT_MAIN};
  width: calc(100% - 190px);
`);

const PaperTitle = styled(Typography)(`
  font-family: Hiragino Sans;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  color: ${COLOR_TEXT_MAIN};
`);

const InnerTitleWrapper = styled('div')(`
  position: relative;
  flex-basis: 100%;
  margin-bottom: 24px;
`);

const FormControlLabelMui = styled(FormControlLabel)(`
  .Mui-checked {
    color: ${COLOR_TEXT_MAIN};
  }
`);

const CheckBoxWrapper = styled('div')(`
  margin-top:12px;
`);

const CheckBoxRowTitle = styled('div')(`
  width: 197.5px;
  display: inline-block;
  font-weight: 700;
  font-size: 14px;
`);

const BoxRowWrapper = styled('div')(`
  margin: 12px 0 0;
  width: 100%;
  position: relative;
`);

const StyledPaper = styled('div')(`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  background: ${COLOR_BUTTON_BACKGROUND};
  box-shadow: 0px 1px 9px ${COLOR_SHADOW};
  border-radius: 8px;
  width: 100%;
  margin: 0 auto 24px auto;
  color: ${COLOR_TEXT_MAIN};
`);

const ListTitleWrapper = styled('div')(`
  position: relative;
  width: 100%;
  height: 32px;
  margin: 48px 0 24px 0;
`);

const SpinnerPosition = styled('div')(`
  display: inline-block;
  marginLeft: 12px;
`);

const ReadMore = styled('div')(`
  width: 200px;
  margin: 0 auto;
`);

const PrimaryButton = styled(Button)(`
  color: ${COLOR_BUTTON_TEXT};
  height: 40px;
  font-size: 14px;
  background: ${COLOR_TEXT_MAIN};
  border-color: ${COLOR_TEXT_MAIN};
  padding: 12px 24px;
  text-transform: none;
  :hover {
    background: ${COLOR_EFFECT};
    border-color: ${COLOR_EFFECT};
  }
`);

const SecondaryButton = styled(Button)(`
  color: ${COLOR_TEXT_MAIN};
  height: 40px;
  padding: 13px 16px;
  font-size: 14px;
  background: ${COLOR_BUTTON_BACKGROUND};
  border: 1.5px solid ${COLOR_BORDER};
  border-radius: 4px;
  margin-right: 16px;
  :hover {
    background: ${COLOR_BUTTON_BACKGROUND};
    color: ${COLOR_EFFECT};
  }
  width: 200px;
`);

const Input = styled('input')(`
  padding: 9px 16px 9px 16px;
  width: 100%;
  border: 1px solid ${COLOR_BORDER};
  border-radius: 4px;
  color: ${COLOR_TEXT_MAIN};
  margin: 16px 0;
  font-size: 14px;
  &:focus, :focus-within, :focus-visible {
    border: 1.5px solid ${COLOR_EFFECT};
    outline-color: ${COLOR_EFFECT};
  }
`);

export default ExperimentsListPage;
